/**
 * The in-browser filesystem the demo runs sibyl against.
 *
 * ⚠️ DEMO ONLY. Every `node:fs` call sibyl makes in the browser lands here: the sample repository
 * trees, the prompts, the triage store and the config all live in this map. Nothing touches a disk.
 */

export interface Entry {
  kind: "file" | "dir"
  data?: string
  mode: number
  mtimeMs: number
}

export const files = new Map<string, Entry>()
files.set("/", { kind: "dir", mode: 0o755, mtimeMs: 0 })

export function norm(p: string): string {
  const abs = p.startsWith("/") ? p : `/work/${p}`
  const out: string[] = []
  for (const part of abs.split("/")) {
    if (part === "" || part === ".") continue
    if (part === "..") out.pop()
    else out.push(part)
  }
  return `/${out.join("/")}`
}

function parent(p: string): string {
  const i = p.lastIndexOf("/")
  return i <= 0 ? "/" : p.slice(0, i)
}

export function enoent(syscall: string, p: string): Error {
  const e = new Error(`ENOENT: no such file or directory, ${syscall} '${p}'`) as Error & { code: string }
  e.code = "ENOENT"
  return e
}

export function mkdirp(p: string): void {
  const n = norm(p)
  if (files.get(n)?.kind === "dir") return
  if (n !== "/") mkdirp(parent(n))
  files.set(n, { kind: "dir", mode: 0o755, mtimeMs: Date.now() })
}

export function put(p: string, data: string, mode = 0o644): void {
  const n = norm(p)
  mkdirp(parent(n))
  files.set(n, { kind: "file", data, mode, mtimeMs: Date.now() })
}

export function get(p: string): Entry | undefined {
  return files.get(norm(p))
}

export function list(dir: string): string[] {
  const n = norm(dir)
  const prefix = n === "/" ? "/" : `${n}/`
  const names = new Set<string>()
  for (const k of files.keys()) {
    if (k !== n && k.startsWith(prefix)) {
      const rest = k.slice(prefix.length)
      if (rest !== "" && !rest.includes("/")) names.add(rest)
    }
  }
  return [...names].sort()
}

export function remove(p: string): void {
  const n = norm(p)
  for (const k of [...files.keys()]) if (k === n || k.startsWith(`${n}/`)) files.delete(k)
}

// ── Persistence of the parts a visitor changes ─────────────────────────────────────────────────
// Only sibyl's own data directory is saved, per browser tab session, so a reload keeps the queue
// and "Reset demo" (or a new tab) starts clean.
const KEY = "sibyl-demo-state-v1"
export const PERSIST = "/home/demo/.local/share/sibyl/"
export function saveState(): void {
  try {
    const out: Record<string, string> = {}
    for (const [k, v] of files) if (k.startsWith(PERSIST) && v.kind === "file") out[k] = v.data ?? ""
    sessionStorage.setItem(KEY, JSON.stringify(out))
  } catch {
    /* storage unavailable: the demo still works, it just forgets on reload */
  }
}
export function loadState(): boolean {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (raw === null) return false
    for (const [k, v] of Object.entries(JSON.parse(raw) as Record<string, string>)) put(k, v)
    return true
  } catch {
    return false
  }
}
export function clearState(): void {
  try {
    sessionStorage.removeItem(KEY)
  } catch {
    /* nothing to clear */
  }
  remove(PERSIST.slice(0, -1))
}
